import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { groupService, userService } from "../services/api";
import { useAuth } from "../contexts/AuthContext";

const GroupDetails = () => {
  const { id } = useParams();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [editGroupDialogOpen, setEditGroupDialogOpen] = useState(false);
  const [deleteGroupDialogOpen, setDeleteGroupDialogOpen] = useState(false);
  const [removeMemberDialogOpen, setRemoveMemberDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("MEMBER");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [groupDescription, setGroupDescription] = useState("");

  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");

        // Fetch group details
        const response = await groupService.getGroup(id);
        setGroup(response.data);
        setGroupName(response.data.name);
        setGroupDescription(response.data.description || "");
      } catch (err) {
        setError("Failed to load group details");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleAddMemberDialogOpen = () => {
    setAddMemberDialogOpen(true);
  };

  const handleAddMemberDialogClose = () => {
    setAddMemberDialogOpen(false);
    setEmail("");
    setRole("MEMBER");
    setSearchResults([]);
  };

  const handleEditGroupDialogOpen = () => {
    setEditGroupDialogOpen(true);
  };

  const handleEditGroupDialogClose = () => {
    setEditGroupDialogOpen(false);
    setGroupName(group.name);
    setGroupDescription(group.description || "");
  };

  const handleDeleteGroupDialogOpen = () => {
    setDeleteGroupDialogOpen(true);
  };

  const handleDeleteGroupDialogClose = () => {
    setDeleteGroupDialogOpen(false);
  };

  const handleRemoveMemberDialogOpen = (member) => {
    setSelectedMember(member);
    setRemoveMemberDialogOpen(true);
  };

  const handleRemoveMemberDialogClose = () => {
    setRemoveMemberDialogOpen(false);
    setSelectedMember(null);
  };

  const handleEmailSearch = async (e) => {
    const value = e.target.value;
    setEmail(value);

    if (value.length < 3) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);
      const response = await userService.searchUsers(value);
      setSearchResults(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAddMember = async () => {
    if (!email) {
      return;
    }

    try {
      const response = await groupService.addUserToGroup(id, email, role);

      // Update group members
      setGroup((prevGroup) => ({
        ...prevGroup,
        members: [...prevGroup.members, response.data],
      }));

      handleAddMemberDialogClose();
    } catch (err) {
      console.error(err);
    }
  };

  const handleRemoveMember = async () => {
    if (!selectedMember) {
      return;
    }

    try {
      await groupService.removeUserFromGroup(id, selectedMember.userId);

      // Update group members
      setGroup((prevGroup) => ({
        ...prevGroup,
        members: prevGroup.members.filter(
          (member) => member.userId !== selectedMember.userId
        ),
      }));

      handleRemoveMemberDialogClose();
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdateGroup = async () => {
    try {
      const response = await groupService.updateGroup(id, {
        name: groupName,
        description: groupDescription,
      });

      setGroup(response.data);
      handleEditGroupDialogClose();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteGroup = async () => {
    try {
      await groupService.deleteGroup(id);
      navigate("/");
    } catch (err) {
      console.error(err);
    }
  };

  const isAdmin =
    group &&
    currentUser &&
    group.members.some(
      (member) => member.userId === currentUser.id && member.role === "ADMIN"
    );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!group) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Group not found
      </Alert>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/")}
        sx={{ mb: 3 }}
      >
        Back to Dashboard
      </Button>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {group.name}
            </Typography>
            {group.description && (
              <Typography variant="body1" color="text.secondary">
                {group.description}
              </Typography>
            )}
          </Box>
          <Box>
            {isAdmin && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<PersonAddIcon />}
                  onClick={handleAddMemberDialogOpen}
                  sx={{ mr: 1 }}
                >
                  Add Member
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleEditGroupDialogOpen}
                  sx={{ mr: 1 }}
                >
                  Edit
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteGroupDialogOpen}
                >
                  Delete
                </Button>
              </>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom>
          Members ({group.members.length})
        </Typography>

        <List>
          {group.members.map((member) => (
            <ListItem key={member.id} divider>
              <ListItemAvatar>
                <Avatar>
                  <PersonIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={member.user.name}
                secondary={member.user.email}
              />
              <Chip
                label={member.role}
                color={member.role === "ADMIN" ? "primary" : "default"}
                size="small"
                sx={{ mr: 2 }}
              />
              {isAdmin && member.userId !== currentUser.id && (
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => handleRemoveMemberDialogOpen(member)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              )}
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Add Member Dialog */}
      <Dialog open={addMemberDialogOpen} onClose={handleAddMemberDialogClose}>
        <DialogTitle>Add Member</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the email of the user you want to add to this group.
          </DialogContentText>

          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            value={email}
            onChange={handleEmailSearch}
            sx={{ mb: 2 }}
          />

          {searchLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {searchResults.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Search Results:
              </Typography>
              <List dense>
                {searchResults.map((user) => (
                  <ListItem
                    key={user.id}
                    button
                    onClick={() => setEmail(user.email)}
                    selected={email === user.email}
                  >
                    <ListItemAvatar>
                      <Avatar>
                        <PersonIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={user.name} secondary={user.email} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <TextField
            select
            label="Role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            fullWidth
          >
            <MenuItem value="MEMBER">Member</MenuItem>
            <MenuItem value="ADMIN">Admin</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddMemberDialogClose}>Cancel</Button>
          <Button
            onClick={handleAddMember}
            variant="contained"
            disabled={!email}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Group Dialog */}
      <Dialog open={editGroupDialogOpen} onClose={handleEditGroupDialogClose}>
        <DialogTitle>Edit Group</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Group Name"
            fullWidth
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={groupDescription}
            onChange={(e) => setGroupDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditGroupDialogClose}>Cancel</Button>
          <Button
            onClick={handleUpdateGroup}
            variant="contained"
            disabled={!groupName}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Group Dialog */}
      <Dialog
        open={deleteGroupDialogOpen}
        onClose={handleDeleteGroupDialogClose}
      >
        <DialogTitle>Delete Group</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this group? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteGroupDialogClose}>Cancel</Button>
          <Button onClick={handleDeleteGroup} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Member Dialog */}
      <Dialog
        open={removeMemberDialogOpen}
        onClose={handleRemoveMemberDialogClose}
      >
        <DialogTitle>Remove Member</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove {selectedMember?.user.name} from
            this group?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRemoveMemberDialogClose}>Cancel</Button>
          <Button onClick={handleRemoveMember} color="error">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GroupDetails;
